/**
 * 공지 서비스 — vsmr.announcement.v1 Connect 클라이언트 래퍼.
 *
 * 운영자가 전체 사용자에게 알리는 글. 게시판과 달리 **전원 공개**라 읽기에
 * 사용자별 필터가 없다. 쓰기·초안 열람이 admin 전용이라는 것은 BFF 가
 * 강제하고, 여기서는 타입 매핑만 한다.
 */

import { createPromiseClient } from "@connectrpc/connect";
import type { PromiseClient } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import { AnnouncementService } from "@/stubs/announcement/announcement_connect";
import {
  CreateAnnouncementRequest,
  DeleteAnnouncementRequest,
  GetAnnouncementRequest,
  ListAnnouncementsRequest,
  MarkAnnouncementsReadRequest,
  UpdateAnnouncementRequest,
  type Announcement as PbAnnouncement,
} from "@/stubs/announcement/announcement_pb";
import { BFF_URL, createAuthInterceptor } from "@/app/services/connectCommon";

const transport = createConnectTransport({
  baseUrl: `${BFF_URL}/api`,
  interceptors: [createAuthInterceptor()],
});

const client: PromiseClient<typeof AnnouncementService> = createPromiseClient(
  AnnouncementService,
  transport,
);

export interface AnnouncementItem {
  id: string;
  number: number;
  authorId: string;
  authorName: string;
  title: string;
  body: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
  /** 초안이면 null */
  publishedAt: string | null;
}

/**
 * "지금 새 공지를 쓰러 간다"를 주소로 나타내는 말.
 *
 * 공지가 0건이면 패널에 목록으로 가는 링크가 없다 — 그 상태에서 관리자가 첫
 * 공지를 쓸 길을 여기로 낸다. 보내는 쪽과 받는 쪽이 같은 이름을 보게 한 곳에
 * 둔다(문자열을 양쪽에 손으로 적으면 한쪽만 바뀐다).
 */
export const COMPOSE_QUERY = 'compose';
export const COMPOSE_PATH = `/announcements?${COMPOSE_QUERY}=1`;
export const isComposeRequested = (search: string): boolean =>
  new URLSearchParams(search).get(COMPOSE_QUERY) === '1';

/**
 * 알린 적이 있는가 — **번호가 붙었는가**로 판정한다.
 *
 * 서버가 동결을 거는 기준과 같은 식이다(bff `_require_not_announced`: `number
 * is not None`). `published` 는 뜻이 다르다 — "지금 모두에게 보이는가"이고,
 * 내려도 번호는 남는다. 그걸로 판정하면 화면이 서버가 거절할 수정·삭제
 * 버튼을 내놓는다. 알린 것인지 묻는 자리는 전부 이 함수를 쓴다.
 */
export const isAnnounced = (a: Pick<AnnouncementItem, 'number'>): boolean => a.number > 0;

export interface AnnouncementList {
  announcements: AnnouncementItem[];
  lastReadNumber: number;
  /** 서버가 센다 — 화면이 세지 않는다 */
  unreadCount: number;
}

function tsToIso(ts?: { seconds: bigint; nanos: number }): string | null {
  if (!ts) return null;
  const ms = Number(ts.seconds) * 1000 + Math.floor(ts.nanos / 1e6);
  return ms > 0 ? new Date(ms).toISOString() : null;
}

function toItem(pb: PbAnnouncement): AnnouncementItem {
  return {
    id: pb.id,
    number: Number(pb.number),
    authorId: pb.authorId,
    authorName: pb.authorName,
    title: pb.title,
    body: pb.body,
    published: pb.published,
    createdAt: tsToIso(pb.createdAt) ?? new Date().toISOString(),
    updatedAt: tsToIso(pb.updatedAt) ?? new Date().toISOString(),
    publishedAt: tsToIso(pb.publishedAt),
  };
}

/** 목록 — 게시된 것. `includeDrafts` 는 admin 만 쓸 수 있고, 아니면 서버가 거절한다. */
export async function listAnnouncements(
  options: { includeDrafts?: boolean } = {},
): Promise<AnnouncementList> {
  const response = await client.listAnnouncements(
    new ListAnnouncementsRequest({ includeDrafts: options.includeDrafts ?? false }),
  );
  return {
    announcements: response.announcements.map(toItem),
    lastReadNumber: Number(response.lastReadNumber),
    unreadCount: response.unreadCount,
  };
}

export async function getAnnouncement(id: string): Promise<AnnouncementItem> {
  const response = await client.getAnnouncement(
    new GetAnnouncementRequest({ announcementId: id }),
  );
  if (!response.announcement) throw new Error("announcement not found");
  return toItem(response.announcement);
}

export async function createAnnouncement(input: {
  title: string;
  body: string;
  published: boolean;
}): Promise<AnnouncementItem> {
  const response = await client.createAnnouncement(new CreateAnnouncementRequest(input));
  if (!response.announcement) throw new Error("empty announcement response");
  return toItem(response.announcement);
}

export async function updateAnnouncement(
  id: string,
  input: { title: string; body: string; published: boolean },
): Promise<AnnouncementItem> {
  const response = await client.updateAnnouncement(
    new UpdateAnnouncementRequest({ announcementId: id, ...input }),
  );
  if (!response.announcement) throw new Error("empty announcement response");
  return toItem(response.announcement);
}

export async function deleteAnnouncement(id: string): Promise<string> {
  const response = await client.deleteAnnouncement(
    new DeleteAnnouncementRequest({ announcementId: id }),
  );
  return response.announcementId;
}

/**
 * 여기까지 읽었다고 표시한다. 서버는 입력값을 게시된 공지 번호의 최댓값으로 자른 뒤(공지가
 * 하나도 없으면 0), 지금 값과 **큰 쪽**을 남긴다 — 옛 공지를 다시 열어도 읽음이 풀리지 않고,
 * 아직 없는 번호가 그 번호까지의 공지를 영영 읽음으로 만들지도 않는다.
 */
export async function markAnnouncementsRead(
  number: number,
): Promise<{ lastReadNumber: number; unreadCount: number }> {
  const response = await client.markAnnouncementsRead(
    new MarkAnnouncementsReadRequest({ number: BigInt(Math.max(number, 0)) }),
  );
  return {
    lastReadNumber: Number(response.lastReadNumber),
    unreadCount: response.unreadCount,
  };
}
